import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, TextField, MenuItem, Button, InputAdornment, IconButton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Snackbar, Alert, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import EnhancedErrorBoundary from '../components/EnhancedErrorBoundary.jsx';
import { TableRowSkeleton } from '../components/SkeletonLoaders.jsx';
import { apiFetch } from '../utils/apiFetch.js';

const statusOptions = [
  '', 'pending', 'paid', 'failed', 'refunded'
];

const sortOptions = [
  { value: 'createdAt_desc', label: 'Newest' },
  { value: 'createdAt_asc', label: 'Oldest' },
  { value: 'amount_desc', label: 'Amount (High to Low)' },
  { value: 'amount_asc', label: 'Amount (Low to High)' },
];

// Pagination defaults
const DEFAULT_PAYMENTS_PER_PAGE = 10;
const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

export default function MoverPayments({ token }) {
  const location = useLocation();
  const [allPayments, setAllPayments] = useState([]); // raw from API
  const [payments, setPayments] = useState([]);        // filtered + sorted
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState('');
  // Filter state - single source of truth
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [sort, setSort] = useState('date_desc');
  const [page, setPage] = useState(1);
  const [paymentsPerPage, setPaymentsPerPage] = useState(DEFAULT_PAYMENTS_PER_PAGE);
  // Parse query params for month/year
  const params = new URLSearchParams(location.search);
  const filterMonth = params.get('month');
  const filterYear = params.get('year');

  // Fetch raw payments once (or when token changes)
  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      setError('');
      try {
        const headers = {};
        if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
        const res = await apiFetch('/api/payments/me', { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        setAllPayments(Array.isArray(data) ? data : []);
      } catch (err) {
        setError('Failed to load payments.');
        setAllPayments([]);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchPayments();
  }, [token]);

  // Re-apply filters whenever any filter state or raw data changes
  useEffect(() => {
    let filtered = [...allPayments];

    // Month/year from query params
    if (filterMonth && filterYear) {
      filtered = filtered.filter(p => {
        const date = p.moveDate ? new Date(p.moveDate) : (p.booking?.moveDate ? new Date(p.booking.moveDate) : null);
        return date && (date.getMonth() + 1 === Number(filterMonth)) && (date.getFullYear() === Number(filterYear));
      });
    }

    // Date range
    if (dateFrom) filtered = filtered.filter(p => {
      const date = p.moveDate ? new Date(p.moveDate) : (p.booking?.moveDate ? new Date(p.booking.moveDate) : null);
      return date && date >= new Date(dateFrom);
    });
    if (dateTo) filtered = filtered.filter(p => {
      const date = p.moveDate ? new Date(p.moveDate) : (p.booking?.moveDate ? new Date(p.booking.moveDate) : null);
      return date && date <= new Date(dateTo);
    });

    // Status
    if (status) filtered = filtered.filter(p => p.status === status);

    // Amount
    if (minAmount) filtered = filtered.filter(p => Number(p.amount) >= Number(minAmount));
    if (maxAmount) filtered = filtered.filter(p => Number(p.amount) <= Number(maxAmount));

    // General search
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(p => {
        const job = (p.jobNumber || p.booking?.jobNumber || '').toString().toLowerCase();
        const name = (p.customerName || p.booking?.user?.name || p.booking?.contactName || '').toLowerCase();
        const email = (p.customerEmail || p.booking?.user?.email || p.booking?.contactEmail || '').toLowerCase();
        const amount = (p.amount || '').toString();
        const stat = (p.status || '').toLowerCase();
        const dateStr = p.moveDate ? new Date(p.moveDate).toLocaleDateString() : '';
        return [job, name, email, amount, stat, dateStr].some(f => f.includes(q));
      });
    }

    // Sorting
    filtered = [...filtered].sort((a, b) => {
      const dateA = a.moveDate ? new Date(a.moveDate) : (a.booking?.moveDate ? new Date(a.booking.moveDate) : new Date(0));
      const dateB = b.moveDate ? new Date(b.moveDate) : (b.booking?.moveDate ? new Date(b.booking.moveDate) : new Date(0));
      if (sort === 'date_desc') return dateB - dateA;
      if (sort === 'date_asc') return dateA - dateB;
      if (sort === 'amount_desc') return Number(b.amount) - Number(a.amount);
      if (sort === 'amount_asc') return Number(a.amount) - Number(b.amount);
      if (sort === 'createdAt_desc') return dateB - dateA;
      if (sort === 'createdAt_asc') return dateA - dateB;
      return 0;
    });

    setPayments(filtered);
    setPage(1); // reset to first page when filters change
  }, [allPayments, search, status, minAmount, maxAmount, dateFrom, dateTo, sort, filterMonth, filterYear]);

  const handleDownload = async (payment) => {
    try {
      const headers = {};
      if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
      const res = await apiFetch(`/api/payments/${payment._id}/invoice`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${payment._id || 'download'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: 'Failed to download invoice.', severity: 'error' });
    }
  };

  // Pagination
  const pageCount = Math.ceil(payments.length / paymentsPerPage);
  const paginatedPayments = payments.slice((page - 1) * paymentsPerPage, page * paymentsPerPage);

  return (
    <EnhancedErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ 
          width: '100%',
          height: '100%',
          p: { xs: 2, md: 3 }, 
          bgcolor: 'background.default',
          overflow: 'auto'
        }}>
          {/* Content wrapper with max width and centering */}
          <Box sx={{
            maxWidth: 1400,
            mx: 'auto',
            width: '100%'
          }}>
          <Typography variant="overline" sx={{ color: '#008D7B', fontWeight: 800, letterSpacing: 1.1 }}>FINANCIALS</Typography>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#173057', mb: .5 }}>Payments</Typography>
          <Typography variant="body2" sx={{ color: '#60748D', mb: 2 }}>Track received payments and download booking invoices.</Typography>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress /></Box>}
        {/* Filters */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 1, border: '1px solid #E4ECF2' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                label="Status"
                value={status}
                onChange={e => setStatus(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                {statusOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt ? opt[0].toUpperCase() + opt.slice(1) : 'All'}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Min Amount"
                type="number"
                value={minAmount}
                onChange={e => setMinAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Max Amount"
                type="number"
                value={maxAmount}
                onChange={e => setMaxAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <DatePicker
                label="From"
                value={dateFrom}
                onChange={setDateFrom}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <DatePicker
                label="To"
                value={dateTo}
                onChange={setDateTo}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Sort By"
                value={sort}
                onChange={e => setSort(e.target.value)}
                sx={{ minWidth: 170 }}
              >
                {sortOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button variant="outlined" color="secondary" fullWidth onClick={() => {
                setSearch(''); setStatus(''); setMinAmount(''); setMaxAmount(''); setDateFrom(null); setDateTo(null); setSort('date_desc');
              }}>Reset</Button>
            </Grid>
          </Grid>
        </Paper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Job #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(payments) && payments.length > 0 ? paginatedPayments.map((p, idx) => (
                <TableRow key={idx}>
                  <TableCell>{p.customerName || p.booking?.user?.name || p.booking?.contactName || ''}{p.customerEmail || p.booking?.user?.email || p.booking?.contactEmail ? ` (${p.customerEmail || p.booking?.user?.email || p.booking?.contactEmail})` : ''}</TableCell>
                  <TableCell>{p.jobNumber || p.booking?.jobNumber || ''}</TableCell>
                  <TableCell>{p.moveDate ? new Date(p.moveDate).toLocaleDateString() : (p.booking?.moveDate ? new Date(p.booking.moveDate).toLocaleDateString() : '')}</TableCell>
                  <TableCell>${p.amount}</TableCell>
                  <TableCell>{p.status}</TableCell>
                  <TableCell><Button onClick={() => handleDownload(p)}>Download</Button></TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">No payments found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Per Page Selector and Pagination Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexWrap: 'wrap', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="payments-per-page-label">Rows per page</InputLabel>
            <Select
              labelId="payments-per-page-label"
              value={paymentsPerPage}
              label="Rows per page"
              onChange={e => { setPaymentsPerPage(Number(e.target.value)); setPage(1); }}
            >
              {PER_PAGE_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
        <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
        </Box>
        </Box>
      </LocalizationProvider>
    </EnhancedErrorBoundary>
  );
}
